import type { Doc } from "building-types/doc";
import type { PlanSetTemplateHandle } from "building-types/handles/docEntityHandleTypes";
import type { Parameter, ParameterValue } from "building-types/options/parameter";
import type { SystemParameterValuesFromDoc } from "building-types/systemParameterTypes";
import type { PlanSetTemplateConfig, PlanSetTemplateIdentifier } from "building-types/views/planSets";
import { isCustomPlanSetTemplateIdentifier } from "building-types/views/planSetTemplateIdentifierTools";
import type { RWTable, Table } from "common/data/table";
import { docEntityGetSortedChildrenFromTable } from "./entity/docEntityGetChildrenFromTable";
import { evaluateParameterDefaultValues } from "./parameterCoreTools";
import { evaluateParameterValueHandles, parameterValuesMerge } from "./parameterTools";
import { extractSystemParameterValuesFromHomeAttributes, systemParametersFromDoc } from "./systemParameterTools";

export function docCollectPlanSetTemplateParameterValues(
  doc: Doc,
  handle: PlanSetTemplateHandle,
  planSetTemplateIdentifier?: PlanSetTemplateIdentifier,
  parameterValues?: Table<ParameterValue>,
): Table<ParameterValue> {
  const planSetTemplate = doc.planSetTemplates[handle.id];

  if (!planSetTemplate) {
    return {};
  }

  const planSetTemplateConfig = docGetPlanSetTemplateIdentifierConfig(doc, handle, planSetTemplateIdentifier);

  if (!planSetTemplateConfig) {
    return {};
  }

  const templateParameterValues: Partial<SystemParameterValuesFromDoc> = { ...parameterValues };
  parameterValuesMerge(templateParameterValues, extractSystemParameterValuesFromHomeAttributes(doc.attributes));

  const { optionChoices, optionSets, globalParameters, attributes } = doc;

  const localParameters: RWTable<Parameter> = docEntityGetSortedChildrenFromTable(
    doc.localParameters,
    handle.id,
  ).reduce((table, localParameter) => {
    table[localParameter.id] = localParameter;
    return table;
  }, {} as RWTable<Parameter>);

  const { systemParameters } = systemParametersFromDoc({ optionChoices, optionSets, attributes });

  // Merge any global/local parameters scoped to the template, including any parameter values associated w/ a specified (or default)template config

  const planSetTemplateParameterValues: Table<ParameterValue> = planSetTemplateConfig
    ? {
        ...evaluateParameterValueHandles(planSetTemplateConfig.globalParameterValues),
        ...evaluateParameterValueHandles(planSetTemplateConfig.parameterValues),
      }
    : {};

  const parameterValuesInScope = evaluateParameterDefaultValues({
    ...systemParameters,
    ...globalParameters,
    ...localParameters,
  });

  parameterValuesMerge(templateParameterValues, parameterValuesInScope);
  parameterValuesMerge(templateParameterValues, planSetTemplateParameterValues);

  return templateParameterValues;
}

export function docGetPlanSetTemplateIdentifierConfig(
  doc: Doc,
  handle: PlanSetTemplateHandle,
  planSetTemplateIdentifier?: PlanSetTemplateIdentifier,
): PlanSetTemplateConfig | undefined {
  if (!doc.planSetTemplates[handle.id]) {
    return undefined;
  }

  const planSetTemplateConfigs: RWTable<PlanSetTemplateConfig> = docEntityGetSortedChildrenFromTable(
    doc.planSetTemplateConfigs,
    handle.id,
  ).reduce((table, planSetTemplateConfig) => {
    table[planSetTemplateConfig.id] = planSetTemplateConfig;
    return table;
  }, {} as RWTable<PlanSetTemplateConfig>);

  const [defaultTemplateConfigId] = Object.keys(planSetTemplateConfigs);

  if (
    planSetTemplateIdentifier &&
    isCustomPlanSetTemplateIdentifier(planSetTemplateIdentifier) &&
    planSetTemplateIdentifier.templateConfigId
  ) {
    return planSetTemplateConfigs[planSetTemplateIdentifier.templateConfigId];
  }

  return planSetTemplateConfigs[defaultTemplateConfigId];
}
